// EventComposer — outer shell + mode tabs. The three modes
// (Single / Batch / AI) live in sister files so each form stays
// independent and small.
//
// Surface choice
//   - Desktop (≥ 720px viewport): doc-ui `Dialog size="lg"` (max ≈ 760px)
//   - Mobile  (< 720px viewport): doc-ui `Drawer side="bottom" size="lg"`
//
// Layout invariants (zero overflow contract)
//   - The shell is a *single* column-flex chain inside the host modal's
//     body. Tabs are flex-shrink:0, the scroll region is the only
//     element that owns `overflow-y`, the footer is flex-shrink:0. No
//     nested scroll containers.
//   - Inside Dialog the shell caps at `min(calc(100dvh - 200px), 640px)`
//     so the dialog never exceeds the viewport.
//   - Inside Drawer the shell takes `height: 100%`; the Drawer's own
//     body padding is removed via the `vq-cal__composer--drawer`
//     modifier so no double-padding appears.
//   - Grid rows collapse via `@container` queries on the shell, so the
//     form responds to its actual container width — works inside
//     narrow embedding too.

import {
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  Dialog,
  Drawer,
  Tabs,
} from '@viquard/doc-ui/components';
import { useCalendarMessages } from '../../i18n.js';
import { DEFAULT_KIND_BEHAVIOR } from '../../utils/kindBehavior.js';
import { DEFAULT_KIND_THEME } from '../../utils/kindTheme.js';
import { AIForm } from './AIForm.js';
import { BatchForm } from './BatchForm.js';
import { SingleForm } from './SingleForm.js';
import type { EventComposerProps, EventComposerSubmitDraft } from './types.js';

// Re-export the public types from the same module the runtime export
// lives in — keeps the package's `index.ts` re-exports stable across
// the file split.
export type { EventComposerProps, EventComposerSubmitDraft };

/**
 * Three-mode event composer:
 *   - **Single**  one event, full form with live preview
 *   - **Batch**   paste many lines, parse + edit list, bulk submit
 *   - **From notes** AI-extracts events from free-form text
 *
 * Every interactive element comes from `@viquard/doc-ui`:
 *   Tabs, Toolbar, Segmented, Input, Textarea, Select, DatePicker,
 *   TimePicker, Switch, Checkbox, Button, IconButton, Tag, Badge,
 *   Empty, List, Dialog, Drawer.
 */
export function EventComposer(props: EventComposerProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    open,
    onOpenChange,
    event,
    draft,
    kindTheme = DEFAULT_KIND_THEME,
    kindBehavior = DEFAULT_KIND_BEHAVIOR,
    groups,
    relatedEvents,
    linkOptionsForKind,
    onSelectRelatedEvent,
    onSubmit,
    onSubmitBatch,
    onDelete,
    defaultDurationMinutes = 60,
    recurrenceEnabled = true,
    remindersEnabled = true,
    locale,
    modes = ['single', 'batch', 'ai'],
    aiExtract,
    viewerTz,
  } = props;

  const editing = event !== null && event !== undefined;
  const [mode, setMode] = useState<'single' | 'batch' | 'ai'>(
    editing ? 'single' : (modes[0] ?? 'single'),
  );

  // Surface picker — viewport based because Dialog/Drawer is portalled
  // outside the calendar's container query scope.
  const mobile = useMobileViewport();

  // Reset mode on open AND whenever the surface flips between
  // desktop/mobile. We force `single` on edit (the other modes are
  // create-only by definition) but keep Batch + AI available on mobile:
  // the drawer expands to a full 100dvh sheet, BatchForm / AIForm both
  // accept `mobile` and adapt their layout, and surfacing the same
  // three modes everywhere matters more than guessing what a phone
  // user "should" be doing.
  useEffect(() => {
    if (!open) return;
    if (editing) setMode('single');
    else setMode((m) => (modes.includes(m) ? m : (modes[0] ?? 'single')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mobile]);

  const showTabs = !editing && modes.length > 1;
  const surfaceClass = ['vq-cal__composer', mobile ? 'vq-cal__composer--drawer' : 'vq-cal__composer--dialog']
    .join(' ');

  // Identity key for SingleForm — changes whenever the editing target
  // OR the open-cycle changes, forcing a remount so all useState seeds
  // re-evaluate. Otherwise `useState(seedXxx)` would only fire once
  // and editing event A then event B would show A's stale fields.
  const singleFormKey = [
    event?.id ?? 'new',
    draft?.startsAt ?? '',
    open ? 'open' : 'closed',
  ].join(':');

  const closeAfter = (action: () => void): (() => void) => () => {
    action();
    onOpenChange(false);
  };

  const submitBatch = (rows: readonly EventComposerSubmitDraft[]): void => {
    if (onSubmitBatch !== undefined) onSubmitBatch(rows);
    else rows.forEach((r) => { onSubmit(r); });
    onOpenChange(false);
  };

  const shell = (
    <div className={surfaceClass} data-mode={mode}>
      {showTabs ? (
        <Tabs
          value={mode}
          onValueChange={(v) => { setMode(v as 'single' | 'batch' | 'ai'); }}
          variant="line"
          size={mobile ? 'sm' : 'md'}
          activation="manual"
          className="vq-cal__composer-tabs"
        >
          <Tabs.List aria-label={messages.composerModeAriaLabel}>
            {modes.includes('single') && (
              <Tabs.Tab value="single">
                <ModeTabBody
                  label={messages.composerModeSingle}
                  sub={messages.composerModeSingleSub}
                  mobile={mobile}
                />
              </Tabs.Tab>
            )}
            {modes.includes('batch') && (
              <Tabs.Tab value="batch">
                <ModeTabBody
                  label={messages.composerModeBatch}
                  sub={messages.composerModeBatchSub}
                  mobile={mobile}
                />
              </Tabs.Tab>
            )}
            {modes.includes('ai') && (
              <Tabs.Tab value="ai">
                <ModeTabBody
                  label={messages.composerModeAi}
                  sub={messages.composerModeAiSub}
                  mobile={mobile}
                />
              </Tabs.Tab>
            )}
          </Tabs.List>
        </Tabs>
      ) : null}
      {mode === 'single' && (
        <SingleForm
          key={singleFormKey}
          event={event}
          draft={draft}
          kindTheme={kindTheme}
          kindBehavior={kindBehavior}
          groups={groups}
          relatedEvents={relatedEvents}
          linkOptionsForKind={linkOptionsForKind}
          onSelectRelatedEvent={onSelectRelatedEvent}
          onSubmit={(d) => {
            onSubmit(d);
            onOpenChange(false);
          }}
          onCancel={closeAfter(() => undefined)}
          onDelete={onDelete}
          defaultDurationMinutes={defaultDurationMinutes}
          recurrenceEnabled={recurrenceEnabled}
          remindersEnabled={remindersEnabled}
          locale={locale}
          mobile={mobile}
          viewerTz={viewerTz}
        />
      )}
      {mode === 'batch' && (
        <BatchForm
          kindTheme={kindTheme}
          kindBehavior={kindBehavior}
          groups={groups}
          onSubmitBatch={submitBatch}
          onCancel={closeAfter(() => undefined)}
          mobile={mobile}
          viewerTz={viewerTz}
          recurrenceEnabled={recurrenceEnabled}
          remindersEnabled={remindersEnabled}
        />
      )}
      {mode === 'ai' && (
        <AIForm
          kindTheme={kindTheme}
          kindBehavior={kindBehavior}
          groups={groups}
          aiExtract={aiExtract}
          onSubmitBatch={submitBatch}
          onCancel={closeAfter(() => undefined)}
          mobile={mobile}
          viewerTz={viewerTz}
          recurrenceEnabled={recurrenceEnabled}
          remindersEnabled={remindersEnabled}
        />
      )}
    </div>
  );

  if (mobile) {
    // The custom className lets `styles.css` override doc-ui Drawer's
    // inline `height: 520px` to a full-viewport sheet (see
    // `.vq-drawer.vq-cal__composer-drawer-shell`). On a phone the
    // 520px half-sheet kept compressing the form into a tight scroll
    // window that the keyboard further halved. A full sheet gives the
    // form room to breathe and matches what users expect from "create
    // event" surfaces in iOS / Google Calendar.
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        side="bottom"
        size="lg"
        modal
        title={editing ? messages.composerEditTitle : messages.composerNewTitle}
        subtitle={
          editing ? messages.composerEditSubtitle : messages.composerNewSubtitle
        }
        className="vq-cal__composer-drawer-shell"
      >
        {shell}
      </Drawer>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      className="vq-cal__composer-dialog-shell"
      title={editing ? messages.composerEditTitle : messages.composerNewTitle}
      subtitle={
        editing ? messages.composerEditSubtitle : messages.composerNewSubtitle
      }
      footerBar={false}
    >
      {shell}
    </Dialog>
  );
}

function useMobileViewport(): boolean {
  const [mobile, setMobile] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 720px)');
    setMobile(mq.matches);
    const handler = (e: MediaQueryListEvent): void => { setMobile(e.matches); };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => { mq.removeEventListener('change', handler); };
    }
    mq.addListener(handler);
    return () => { mq.removeListener(handler); };
  }, []);
  return mobile;
}

function ModeTabBody(props: {
  label: string;
  sub: string;
  mobile: boolean;
}): ReactNode {
  // On mobile we drop both the 28×28 icon box and the .tab-text stack
  // wrapper — the sub-label was already hidden, so the wrapper added
  // markup without any visual job. Three label-only tabs ("Single",
  // "Batch", "From notes") fit a 360px drawer comfortably.
  if (props.mobile) {
    return (
      <span className="vq-cal__composer-tab-body">
        <span className="vq-cal__composer-tab-label">{props.label}</span>
      </span>
    );
  }
  return (
    <span className="vq-cal__composer-tab-body">
      <span className="vq-cal__composer-tab-text">
        <span className="vq-cal__composer-tab-label">{props.label}</span>
        <span className="vq-cal__composer-tab-sub">{props.sub}</span>
      </span>
    </span>
  );
}
